import type { FC } from 'react';
import { useState } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import AddressFinderWidget, { addressFinderSchema, defaultValues as addressFinderDefaultValues } from '@eneco/components/AddressFinder/AddressFinder';
import { useAddressFinderQueryStringDefaultValues } from '@eneco/components/AddressFinder/hooks/useAddressFinderQueryStringDefaultValues';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRedirect } from '@eneco/hooks/redirect';
import { Box, Button, ButtonLink, Form, Stack, Text, TextLink } from '@eneco/sparky';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import type { AddressFinderContent } from './address-finder-content.types';
import { toAddressFinderCustomLabels } from './address-finder-custom-labels';
import { EnergyUsagePurposeSelector } from './EnergyUsagePurposeSelector';

interface FormProps {
  city?: string;
  houseNumber?: string;
  houseNumberSuffix?: string;
  postalCode?: string;
  street?: string;
  energyUsagePurpose?: string;
}

/** Shared with `SmeAddressFinder`, whose content carries the same ADR-0068 role-collapse. */
export const findByRole = <T extends { role?: string }>(entries: T[] | undefined, role: string): T | undefined =>
  entries?.find(entry => entry.role === role);

/**
 * `AddressFinder` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/AddressFinder/AddressFinder.tsx`):
 * the shared `@eneco/components/AddressFinder` widget for the postal-code/house-number lookup, an
 * energy-usage-purpose radio group beneath it, an optional disclaimer, and up to three actions (submit
 * button, secondary button, secondary text link) — submitting redirects to the button link, or to
 * `energyUsagePurposeRedirectLink` when "business" is selected.
 *
 * See `address-finder-content.types.ts` for the (partial) content shape — the energy-usage-purpose radio
 * options and any internal-reference link have no resolvable content on `?_cms=sanity` yet, and every
 * ADR-0068 role-collapsed field falls back to its raw Sitecore name since that lane's adapter does not
 * perform the collapse.
 */
const AddressFinder: FC = () => {
  const { adapter } = useCms();
  const content = useContent<AddressFinderContent>();
  const addressFinderQueryStringDefaultValues = useAddressFinderQueryStringDefaultValues();
  const redirect = useRedirect();
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);

  // `libs/cms-sitecore`'s adapter does no ADR-0068 role-collapse, so on `?_cms=sitecore` these arrays
  // are always empty and the raw Sitecore field names (`content.houseNumberFormField`, etc.) carry the
  // real data instead — each `??` falls back to that raw field.
  const houseNumberFormField = findByRole(content.formFields, 'houseNumberFormField') ?? content.houseNumberFormField;
  const houseNumberSuffixFormField = findByRole(content.formFields, 'houseNumberSuffixFormField') ?? content.houseNumberSuffixFormField;
  const postalCodeFormField = findByRole(content.formFields, 'postalCodeFormField') ?? content.postalCodeFormField;
  const notFoundErrorNotification = findByRole(content.notifications, 'notFoundErrorNotification') ?? content.notFoundErrorNotification;
  const serverErrorNotification = findByRole(content.notifications, 'serverErrorNotification') ?? content.serverErrorNotification;
  const energyUsagePurposeList = findByRole(content.radioGroups, 'energyUsagePurposeList') ?? content.energyUsagePurposeList;

  const customLabels = toAddressFinderCustomLabels(adapter, {
    houseNumberFormField,
    houseNumberSuffixFormField,
    postalCodeFormField,
    notFoundErrorNotification,
    serverErrorNotification,
  });

  const formSchema = yup.object({
    ...addressFinderSchema(isSuffixRequired).fields,
    ...(energyUsagePurposeList?.requiredMessage
      ? {
          energyUsagePurpose: yup.string().required(energyUsagePurposeList.requiredMessage).notOneOf(['none'], energyUsagePurposeList.requiredMessage),
        }
      : {}),
  });

  const formMethods = useForm<FormProps>({
    defaultValues: {
      ...addressFinderDefaultValues,
      ...addressFinderQueryStringDefaultValues,
      energyUsagePurpose: content.energyUsagePurposeDefaultOption ?? 'none',
    },
    mode: 'onBlur',
    resolver: typedYupResolver<FormProps>(formSchema),
  });

  const { handleSubmit, control, watch } = formMethods;
  const { energyUsagePurpose } = watch();

  const buttonLink = linkWithUrl(content.buttonLink);
  const secondaryButtonLink = linkWithUrl(content.secondaryButtonLink);
  const secondaryLink = linkWithUrl(content.secondaryLink);
  const energyUsagePurposeRedirectLink = linkWithUrl(content.energyUsagePurposeRedirectLink);

  const onSubmit = ({ houseNumber, postalCode, houseNumberSuffix }: FormProps) => {
    const linkToUse = energyUsagePurpose === 'business' && energyUsagePurposeRedirectLink?.href ? energyUsagePurposeRedirectLink : buttonLink;

    redirect({
      queryParams: { postalCode: postalCode?.replace?.(' ', ''), houseNumber, houseNumberSuffix, energyUsagePurpose },
      // `useRedirect` treats a bare string as always a full `window.location` navigation — passing the
      // resolved `linkType` instead lets an internal target go through client-side routing.
      link: linkToUse?.href
        ? { href: linkToUse.href, linktype: linkToUse.linkType ?? 'internal', target: linkToUse.target ?? '', querystring: '', anchor: '' }
        : undefined,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <AddressFinderWidget
          customLabels={customLabels}
          isSuffixRequired={isSuffixRequired}
          setIsSuffixRequired={setIsSuffixRequired}
          addressGroupLabel={content.addressGroupLabel ?? 'Adres invoer'}
        />

        {energyUsagePurposeList && (
          <EnergyUsagePurposeSelector control={control} label={energyUsagePurposeList.label ?? ''} options={energyUsagePurposeList.options ?? []} direction="row" />
        )}

        {content.disclaimerText && (
          <Box paddingTop="4">
            <Text color="textLowEmphasis">{content.disclaimerText}</Text>
          </Box>
        )}

        {buttonLink?.href && (
          <Box paddingTop="6">
            <Stack gap="4" alignY="center" direction={{ initial: 'column', lg: 'row' }} inline={{ initial: false, lg: true }} wrap>
              <Button size="regular" type="submit" action="primary">
                {buttonLink.text}
              </Button>
              {secondaryButtonLink?.href && (
                <ButtonLink href={secondaryButtonLink.href} size="regular" action="secondary">
                  {secondaryButtonLink.text}
                </ButtonLink>
              )}
              {!secondaryButtonLink?.href && secondaryLink?.href && (
                <TextLink emphasis="high" href={secondaryLink.href}>
                  {secondaryLink.text}
                </TextLink>
              )}
            </Stack>
          </Box>
        )}
      </Form>
    </FormProvider>
  );
};

export default AddressFinder;
